import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../hooks/useAuth';
import pettyCashService from '../../../services/pettyCashService';

const PettyCashTransactionForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  transactionType = 'expense', // 'expense' or 'replenishment'
  initialData = null 
}) => {
  const { user, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date()?.toISOString()?.split('T')?.[0],
    reference: '',
    notes: '',
    expenseAccountId: '', // For expenses
    fundingAccountId: ''   // For replenishments
  });
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [fundingAccounts, setFundingAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const organizationId = userProfile?.organization_id;
  const isExpense = transactionType === 'expense';
  const isReplenishment = transactionType === 'replenishment';

  // Load form data on open
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          amount: initialData?.amount?.toString() || '',
          description: initialData?.description || '',
          date: initialData?.date || new Date()?.toISOString()?.split('T')?.[0],
          reference: initialData?.reference || '',
          notes: initialData?.notes || '',
          expenseAccountId: initialData?.expenseAccountId || '',
          fundingAccountId: initialData?.fundingAccountId || ''
        });
      } else {
        // Reset form for new transaction
        setFormData({
          amount: '',
          description: '',
          date: new Date()?.toISOString()?.split('T')?.[0],
          reference: '',
          notes: '',
          expenseAccountId: '',
          fundingAccountId: ''
        });
      }
      setErrors({});
      loadAccounts();
    }
  }, [isOpen, initialData, transactionType]);

  // Load accounts based on transaction type
  const loadAccounts = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      
      if (isExpense) {
        const accounts = await pettyCashService?.getExpenseAccounts(organizationId);
        setExpenseAccounts(accounts || []);
      }
      
      if (isReplenishment) {
        const accounts = await pettyCashService?.getFundingAccounts(organizationId);
        setFundingAccounts(accounts || []);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.amount || parseFloat(formData?.amount) <= 0) {
      newErrors.amount = 'Amount is required and must be greater than 0';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData?.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    if (!organizationId || !user?.id) {
      setErrors({ submit: 'Authentication error. Please try again.' });
      return;
    }

    try {
      setSubmitting(true);
      setErrors({});

      const transactionData = {
        amount: parseFloat(formData?.amount),
        description: formData?.description?.trim(),
        date: formData?.date,
        reference: formData?.reference?.trim() || null,
        notes: formData?.notes?.trim() || null,
        expenseAccountId: formData?.expenseAccountId || null,
        fundingAccountId: formData?.fundingAccountId || null
      };

      let result;
      if (isExpense) {
        result = await pettyCashService?.createPettyCashExpense(
          organizationId,
          user?.id,
          transactionData
        );
      } else {
        result = await pettyCashService?.createPettyCashReplenishment(
          organizationId,
          user?.id,
          transactionData
        );
      }

      if (result?.success) {
        onSubmit?.(result);
        onClose?.();
      } else {
        throw new Error(result?.message || 'Transaction creation failed');
      }
    } catch (error) {
      console.error('Error submitting transaction:', error);
      setErrors({ submit: error?.message || 'Failed to create transaction. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      amount: '',
      description: '',
      date: new Date()?.toISOString()?.split('T')?.[0],
      reference: '',
      notes: '',
      expenseAccountId: '',
      fundingAccountId: ''
    });
    setErrors({});
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">
                {isExpense ? 'Record Petty Cash Expense' : 'Add Petty Cash Replenishment'}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={handleClose}
                disabled={submitting}
              />
            </div>
          </div>

          {/* Form Body */}
          <div className="p-6 space-y-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Amount *
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData?.amount}
                onChange={(e) => handleInputChange('amount', e?.target?.value)}
                error={errors?.amount}
                disabled={submitting}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description *
              </label>
              <Input
                type="text"
                placeholder={isExpense ? 'What was this expense for?' : 'Replenishment source'}
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                error={errors?.description}
                disabled={submitting}
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Date *
              </label>
              <Input
                type="date"
                value={formData?.date}
                onChange={(e) => handleInputChange('date', e?.target?.value)}
                error={errors?.date}
                disabled={submitting}
              />
            </div>

            {/* Account Selection for Expenses */}
            {isExpense && expenseAccounts?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Expense Category
                </label>
                <Select
                  value={formData?.expenseAccountId}
                  onChange={(value) => handleInputChange('expenseAccountId', value)}
                  placeholder="Select expense category (optional)"
                  disabled={loading || submitting}
                  options={[
                    { value: '', label: 'General Petty Cash Expense' },
                    ...expenseAccounts?.map(account => ({
                      value: account?.id,
                      label: `${account?.account_code} - ${account?.account_name}`
                    }))
                  ]}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave blank to record as general petty cash expense
                </p>
              </div>
            )}

            {/* Account Selection for Replenishments */}
            {isReplenishment && fundingAccounts?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Funding Account
                </label>
                <Select
                  value={formData?.fundingAccountId}
                  onChange={(value) => handleInputChange('fundingAccountId', value)}
                  placeholder="Select funding source (optional)"
                  disabled={loading || submitting}
                  options={[
                    { value: '', label: 'General Cash/Bank Account' },
                    ...fundingAccounts?.map(account => ({
                      value: account?.id,
                      label: `${account?.account_code} - ${account?.account_name} (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(account?.current_balance || 0)})`
                    }))
                  ]}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Choose which account is funding this replenishment
                </p>
              </div>
            )}

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Reference Number
              </label>
              <Input
                type="text"
                placeholder="Receipt #, Invoice #, etc."
                value={formData?.reference}
                onChange={(e) => handleInputChange('reference', e?.target?.value)}
                disabled={submitting}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes
              </label>
              <textarea
                className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="3"
                placeholder="Additional notes (optional)"
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                disabled={submitting}
              />
            </div>

            {/* Error Display */}
            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <p className="text-error text-sm">{errors?.submit}</p>
              </div>
            )}

            {/* Transaction Type Info */}
            <div className={`rounded-lg p-3 ${
              isExpense ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}>
              <div className="flex items-center">
                <span className={`text-lg mr-2 ${
                  isExpense ? 'text-red-600' : 'text-green-600'
                }`}>
                  {isExpense ? '💸' : '💰'}
                </span>
                <div>
                  <p className={`text-sm font-medium ${
                    isExpense ? 'text-red-900' : 'text-green-900'
                  }`}>
                    {isExpense ? 'Recording Expense' : 'Adding Replenishment'}
                  </p>
                  <p className={`text-xs ${
                    isExpense ? 'text-red-700' : 'text-green-700'
                  }`}>
                    {isExpense 
                      ? 'This will decrease your petty cash balance' :'This will increase your petty cash balance'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={submitting}
              iconName={isExpense ? 'Minus' : 'Plus'}
              iconPosition="left"
            >
              {isExpense ? 'Record Expense' : 'Add Replenishment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PettyCashTransactionForm;